import { useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ModernCamera } from "@/components/ui/modern-camera";
import { useIsMobile } from "@/hooks/useMobile";
import { X } from "lucide-react";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";

interface ModernCameraCaptureProps {
  onCapture: (imageData: string) => void;
  onClose: () => void;
}

export default function ModernCameraCapture({ onCapture, onClose }: ModernCameraCaptureProps) {
  const { i18n } = useLingui();
  const isMobile = useIsMobile();
  const [isCapturing, setIsCapturing] = useState(false);

  const instructions = {
    title: i18n._(t`Ready to Take Photos`),
    description: i18n._(
      t`Position your document or photo within the frame and click the capture button`,
    ),
  };

  const handleCapture = (imageData: string) => {
    setIsCapturing(true);
    onCapture(imageData);
  };

  // Portal to document.body: v2 CardShell uses transform animations, which break
  // `position: fixed` for descendants (same issue as dental upload progress modal).
  const overlay = isMobile ? (
    <ModernCamera
      onCapture={handleCapture}
      onClose={onClose}
      isFullscreen={true}
      overlayGuide={{
        width: "12rem",
        height: "14rem",
        shape: "rectangle",
      }}
      instructions={instructions}
      autoCapture={false}
      autoStart={true}
      disabled={isCapturing}
    />
  ) : (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm sm:p-4">
      <Card className="relative z-[201] w-full max-w-[min(40rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-2xl sm:max-w-lg">
        <CardContent className="p-0">
          <div className="relative border-b border-gray-100 px-4 pb-3 pt-4 pr-14 sm:px-5 sm:pb-4 sm:pt-5 sm:pr-16">
            <h2 className="font-nunito text-lg font-semibold leading-snug text-gray-900">
              {instructions.title}
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-gray-600">
              {instructions.description}
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="absolute right-3 top-3 h-10 w-10 rounded-full p-0 text-gray-500 hover:bg-gray-100 hover:text-gray-900 sm:right-4 sm:top-4"
              aria-label={i18n._(t`Close`)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="px-4 pb-5 pt-4 sm:px-5 sm:pb-6">
            <ModernCamera
              onCapture={handleCapture}
              isFullscreen={false}
              dialogLayout
              className="w-full"
              overlayGuide={{
                width: "12rem",
                height: "14rem",
                shape: "rectangle",
              }}
              instructions={instructions}
              autoCapture={false}
              autoStart={true}
              disabled={isCapturing}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(overlay, document.body);
}
